/**
 * Transcript loading and single-reel transcription (OutlierOS "⚡ AI Transcribe" / "Unlock free transcript"):
 * existing transcripts open for 0 credits; otherwise a job is queued and polled until done or failed
 * (credits are refunded on failure).
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from '@vw/ui';
import { TOO_LARGE_TO_TRANSCRIBE, TRANSCRIPTION_NOT_SET_UP_JOB, VIDEO_LENGTH_MISMATCH, isLengthMismatch } from '@vw/core/src/groq';
import { busyQueueMessage, type QueueInfo } from '@vw/core/src/transcription-queue';
import { api, ApiError } from '../../api';
import { navigate } from '../../router';
import type { Transcript } from './types';

const FAIL: Record<string, string> = {
  no_credits: "You're out of transcription credits for this billing period. They reset on your billing date (see Billing).",
  busy: 'Too many transcriptions running; try again in a minute.',
  unavailable: "This creator's transcripts aren't available.",
  not_found: "This reel isn't available any more.",
  needs_video: "This reel's video isn't saved yet. Open the creator's Reels tab on Instagram with the Chrome extension and transcribe it from the side panel (the extension fetches the video). Nothing was charged.",
  video_paused: 'Saving videos is paused right now; try again later. Nothing was charged.',
};

export function useTranscript(pk: string, hasTranscript: boolean, onDone?: (pk: string, t: Transcript) => void) {
  const [t, setT] = useState<Transcript | null | 'loading'>(hasTranscript ? 'loading' : null);
  const [job, setJob] = useState<string | null>(null);
  /** Decision 84: the busy queue's position and ETA while the job waits (updated by each poll). */
  const [queue, setQueue] = useState<QueueInfo | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const pkRef = useRef(pk);
  pkRef.current = pk;

  useEffect(() => {
    setMessage(null);
    setJob(null);
    setQueue(null);
    if (!hasTranscript) return setT(null);
    setT('loading');
    let live = true;
    api<Transcript>(`/api/reels/${pk}/transcript`).then((x) => live && setT(x), (e) => {
      if (!live) return;
      setT(null);
      if (e instanceof ApiError && e.status === 404) setMessage(e.message);
    });
    return () => { live = false; };
  }, [pk, hasTranscript]);

  useEffect(() => {
    if (!job) return;
    let live = true;
    const timer = setInterval(async () => {
      try {
        const { jobs } = await api<{ jobs: Array<{ status: string; error_code: string | null; queue?: QueueInfo }> }>(`/api/transcribe/jobs?ids=${job}`);
        if (!live) return;
        const j = jobs[0];
        setQueue(j?.status === 'queued' && j.queue ? j.queue : null);
        if (j?.status === 'done') {
          setJob(null);
          if (j.error_code === 'no_speech') return setMessage('No speech found in this reel.');
          const x = await api<Transcript>(`/api/reels/${pk}/transcript`).catch(() => null);
          if (!live) return;
          setT(x);
          if (x) done.current?.(pk, x);
          else setMessage("The transcript isn't available for this reel.");
        } else if (j?.status === 'failed') {
          setJob(null);
          setMessage(j.error_code === 'needs_video'
            ? "The reel's audio couldn't be read, so it wasn't transcribed. Your credits were refunded."
            : j.error_code === 'too_large' ? TOO_LARGE_TO_TRANSCRIBE
            : j.error_code === 'transcription_unavailable' ? TRANSCRIPTION_NOT_SET_UP_JOB
            : isLengthMismatch(j.error_code) ? VIDEO_LENGTH_MISMATCH
            : "Transcription didn't work for this reel. Your credits were refunded.");
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { live = false; clearInterval(timer); };
  }, [job, pk]);

  async function transcribe() {
    const forPk = pk;
    setMessage(null);
    try {
      const r = await api<{ items: Array<{ status: string; jobId?: string; queue?: QueueInfo }> }>('/api/transcribe', { method: 'POST', json: { reelPks: [forPk] } });
      // The inspector may have moved to another reel meanwhile; the job still runs and shows when they come back.
      if (pkRef.current !== forPk) return;
      const item = r.items[0];
      if (item?.status === 'done') {
        const x = await api<Transcript>(`/api/reels/${forPk}/transcript`);
        if (pkRef.current !== forPk) return;
        setT(x);
        done.current?.(forPk, x);
      } else if (item?.status === 'queued' && item.jobId) {
        setQueue(item.queue ?? null);
        setJob(item.jobId);
      }
      else {
        const m = FAIL[item?.status ?? ''] ?? 'Could not start the transcription.';
        toast.error(m, item?.status === 'no_credits' ? { action: { label: 'See plans', onClick: () => navigate('/billing') } } : undefined);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not start the transcription.');
    }
  }

  // "Our servers are busy right now — your reel is in the queue and will be transcribed in about N minutes."
  const queueMessage = job && queue ? busyQueueMessage(queue.etaMinutes) : null;
  return { transcript: t, job, message, transcribe, queueMessage };
}
